import React, { useEffect, useMemo, useState } from 'react';
import { api, formatEUR, formatDate } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Send, CheckCircle2, XCircle, Trash2, CreditCard, AlertTriangle, Loader2, CalendarPlus } from 'lucide-react';
import { toast } from 'sonner';
import DarCitaDialog from './citas/DarCitaDialog';
import { PAYMENT_METHODS } from '../lib/cobroQuickHelpers';
import {
  amountOptionsForConcept, canonicalCobroConcept, suggestedAmountForConcept, useCobroPresets,
} from '../lib/cobroPresets';

/**
 * Phase 24.D — Dialog para gestionar el ciclo de vida CRM de un presupuesto.
 *
 * Estados soportados (en orden de avance):
 *   draft        → presupuesto creado pero no enviado
 *   sent         → enviado al paciente (pendiente de respuesta)
 *   accepted     → paciente lo ha aceptado → registrar pagos parciales
 *   rejected     → paciente lo ha rechazado → registrar motivo
 *
 * También mantiene compatibilidad con los estados antiguos:
 *   pendiente, confirmado (≈ accepted), cancelado (≈ rejected), expirado.
 *
 * Si el presupuesto está aceptado/confirmado, muestra el panel de pagos:
 *   total presupuesto · total pagado · pendiente · líneas registradas.
 */
export default function BudgetCrmDialog({ open, onOpenChange, budget, onChanged }) {
  const [bData, setBData] = useState(budget);
  const [payments, setPayments] = useState(null);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [newPayment, setNewPayment] = useState({
    amount: '', payment_method: '', concept: '', date: new Date().toISOString().slice(0, 10),
  });
  // Al aceptar el presupuesto: ventana para agendar la cirugía directamente
  // (cita de quirófano con el primer hueco disponible ya ofrecido).
  const [agendarOpen, setAgendarOpen] = useState(false);

  useEffect(() => {
    setBData(budget);
    setRejectReason('');
  }, [budget, open]);

  const isAccepted = useMemo(() => {
    const s = bData?.status;
    return s === 'accepted' || s === 'confirmado';
  }, [bData?.status]);

  const isRejected = useMemo(() => {
    const s = bData?.status;
    return s === 'rejected' || s === 'cancelado';
  }, [bData?.status]);

  const isSent = useMemo(() => bData?.status === 'sent', [bData?.status]);
  // Los atajos de concepto son los de Configuración (sin ellos, a mano).
  const cobroPresets = useCobroPresets();
  const paymentAmountOptions = amountOptionsForConcept(newPayment.concept, cobroPresets);

  const updatePaymentConcept = (concept) => {
    const suggested = suggestedAmountForConcept(concept, cobroPresets);
    setNewPayment((current) => ({
      ...current,
      concept,
      amount: suggested !== null ? suggested : current.amount,
    }));
  };

  const reloadPayments = async () => {
    if (!budget?.id) return;
    setLoadingPayments(true);
    try {
      const r = await api.get(`/api/budgets/${budget.id}/payments`);
      setPayments(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudieron cargar los pagos');
    } finally {
      setLoadingPayments(false);
    }
  };

  useEffect(() => {
    if (open && isAccepted) {
      reloadPayments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isAccepted, budget?.id]);

  const refreshBudget = async () => {
    if (!budget?.id) return;
    const r = await api.get('/api/budgets', { params: { patient_id: budget.patient_id } });
    const fresh = (r.data || []).find((x) => x.id === budget.id);
    if (fresh) setBData(fresh);
  };

  const markSent = async () => {
    setBusy(true);
    try {
      await api.post(`/api/budgets/${budget.id}/mark-sent`);
      toast.success('Marcado como enviado');
      await refreshBudget();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al marcar enviado');
    } finally {
      setBusy(false);
    }
  };

  const markAccepted = async () => {
    setBusy(true);
    try {
      await api.post(`/api/budgets/${budget.id}/mark-accepted`);
      toast.success('Marcado como aceptado');
      await refreshBudget();
      reloadPayments();
      onChanged?.();
      // Directamente a agendar la cirugía (se puede cerrar y hacerlo luego).
      setAgendarOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al marcar aceptado');
    } finally {
      setBusy(false);
    }
  };

  // La cirugía quedó agendada → guardar la fecha en el presupuesto.
  const onCirugiaAgendada = async (cita) => {
    if (cita?.date) {
      try {
        await api.put(`/api/budgets/${budget.id}`, { surgery_date: cita.date });
        setBData((prev) => (prev ? { ...prev, surgery_date: cita.date } : prev));
      } catch {
        // no bloquea: la cita ya está creada
      }
    }
    onChanged?.();
  };

  const markRejected = async () => {
    if (!rejectReason.trim()) {
      toast.error('Indica el motivo del rechazo');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/api/budgets/${budget.id}/reject`, { reason: rejectReason.trim() });
      toast.success('Marcado como rechazado');
      await refreshBudget();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al marcar rechazado');
    } finally {
      setBusy(false);
    }
  };

  const addPayment = async () => {
    const amt = Number(newPayment.amount || 0);
    if (amt <= 0) {
      toast.error('Indica un importe mayor que 0');
      return;
    }
    if (!newPayment.payment_method) {
      toast.error('Elige el método de pago');
      return;
    }
    setBusy(true);
    try {
      await api.post('/api/cobros', {
        patient_id: budget.patient_id,
        patient_name: budget.patient_name,
        nif: '',
        concept: canonicalCobroConcept(newPayment.concept, cobroPresets) || `Pago de ${budget.number}`,
        amount: amt,
        vat_applied: false,
        payment_method: newPayment.payment_method,
        date: newPayment.date,
        budget_id: budget.id,
      });
      toast.success('Pago registrado');
      setNewPayment({ amount: '', payment_method: '', concept: '', date: new Date().toISOString().slice(0, 10) });
      await reloadPayments();
      try {
        await onChanged?.();
      } catch {
        // El cobro ya está guardado; un fallo al refrescar la lista exterior
        // no debe desmontar ni dejar en blanco este diálogo.
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al registrar el pago');
    } finally {
      setBusy(false);
    }
  };

  const deleteCobro = async (cobroId) => {
    if (!window.confirm('¿Eliminar este pago?')) return;
    setBusy(true);
    try {
      await api.delete(`/api/cobros/${cobroId}`);
      toast.success('Pago eliminado');
      reloadPayments();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al eliminar');
    } finally {
      setBusy(false);
    }
  };

  if (!bData) return null;

  const devueltoSinPresupuesto = Number(payments?.devuelto_sin_presupuesto || 0);
  // Lo devuelto por encima de lo cobrado en este presupuesto (informe
  // 22/9/2026, dinero-MEDIA-2): el servidor ya no manda un «pagado» negativo,
  // lo manda aparte para que aquí se diga en rojo.
  const devueltoDeMas = Number(payments?.devuelto_de_mas || 0);
  const cobrosDeOtraFicha = payments?.cobros_de_otra_ficha || [];
  const pendienteAlDia = !!payments && !(payments.pending > 0) && !(devueltoSinPresupuesto > 0)
    && !(devueltoDeMas > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>Seguimiento {bData.number}</span>
            <span className="text-sm text-muted-foreground font-normal">· {bData.patient_name}</span>
          </DialogTitle>
        </DialogHeader>

        {/* ESTADO ACTUAL */}
        <div className="rounded-lg border border-border p-3 bg-[rgba(46,110,94,0.03)]">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm">
              <span className="text-muted-foreground">Estado actual:</span>{' '}
              <strong className="text-[#13231E]">{bData.status || 'draft'}</strong>
              {bData.sent_at && <span className="text-xs text-muted-foreground ml-2">· enviado {formatDate(bData.sent_at)}</span>}
              {bData.accepted_at && <span className="text-xs text-muted-foreground ml-2">· aceptado {formatDate(bData.accepted_at)}</span>}
              {bData.rejected_at && <span className="text-xs text-muted-foreground ml-2">· rechazado {formatDate(bData.rejected_at)}</span>}
              {/* D1 (22/9/2026): lo aceptó el cobro de la reserva y
                  ya no queda dinero detrás, así que vuelve a enviado. Se dice
                  por qué, para que nadie lo lea como un presupuesto sin
                  respuesta. El motivo lo escribe el servidor según lo que
                  pasó (una devolución, un cobro borrado o corregido, un cobro
                  de otra ficha) y se pinta tal cual: un «se devolvió todo»
                  fijo mentía cuando nadie había devuelto nada. Sin motivo
                  guardado, solo la fecha. Vale también si después caducó. */}
              {['sent', 'expirado'].includes(bData.status) && bData.desaceptado_at && (
                <span className="text-xs text-amber-700 ml-2" data-testid="budget-crm-desaceptado">
                  · deja de estar aceptado el {formatDate(bData.desaceptado_at)}
                  {bData.desaceptado_motivo ? `: ${bData.desaceptado_motivo}` : ''}
                </span>
              )}
            </div>
            <div className="text-sm font-semibold text-[#13231E] tabular-nums">
              {formatEUR(bData.total)}
            </div>
          </div>
          {bData.rejected_reason && (
            <div className="mt-2 text-xs bg-[rgba(220,53,69,0.08)] border border-[rgba(220,53,69,0.25)] rounded-md p-2 text-[#7a1f1f]">
              <strong>Motivo del rechazo:</strong> {bData.rejected_reason}
            </div>
          )}
        </div>

        {/* ACCIONES PRINCIPALES */}
        {!isAccepted && !isRejected && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-3">
            {!isSent && (
              <Button onClick={markSent} disabled={busy} variant="outline" className="border-[#2E6E5E] text-[#13231E]" data-testid="budget-crm-mark-sent">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Marcar Enviado
              </Button>
            )}
            <Button onClick={markAccepted} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="budget-crm-mark-accepted">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Marcar Aceptado
            </Button>
            <Button
              onClick={() => {
                const reason = window.prompt('Motivo del rechazo:');
                if (reason) {
                  setRejectReason(reason);
                  setTimeout(() => markRejected(), 0);
                }
              }}
              disabled={busy}
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-50"
              data-testid="budget-crm-mark-rejected"
            >
              <XCircle className="w-4 h-4 mr-2" />
              Marcar Rechazado
            </Button>
          </div>
        )}

        {/* COMENTARIO DE RECHAZO (sólo si está marcado para rechazo manual) */}
        {!isAccepted && !isRejected && (
          <div className="mt-3">
            <Label className="text-xs text-muted-foreground">
              (Opcional) Motivo de rechazo si vas a marcar el presupuesto como rechazado
            </Label>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Ej: precio elevado, lo va a pensar, opta por otra clínica..."
              rows={2}
              data-testid="budget-crm-reject-reason"
            />
            {rejectReason && (
              <Button size="sm" variant="outline" className="mt-2 border-red-300 text-red-700" onClick={markRejected} disabled={busy}>
                Confirmar rechazo con este motivo
              </Button>
            )}
          </div>
        )}

        {/* AGENDAR CIRUGÍA — si aceptado (también accesible más tarde) */}
        {isAccepted && (
          <div className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-[#2E6E5E]/30 bg-[rgba(46,110,94,0.05)] p-3">
            <div className="text-sm text-[#13231E]">
              <span className="font-semibold">Cirugía:</span>{' '}
              {bData.surgery_name || '—'}
              {bData.surgery_date
                ? <span className="text-emerald-700 font-medium"> · agendada el {formatDate(bData.surgery_date)}</span>
                : <span className="text-muted-foreground"> · sin agendar</span>}
            </div>
            <Button
              size="sm"
              onClick={() => setAgendarOpen(true)}
              className="bg-[#2E6E5E] hover:bg-[#1E4A3F] shrink-0"
              data-testid="budget-crm-agendar-cirugia"
            >
              <CalendarPlus className="w-4 h-4 mr-1.5" /> Agendar cirugía
            </Button>
          </div>
        )}

        {/* PANEL DE PAGOS — si aceptado */}
        {isAccepted && (
          <div className="mt-4 border-t border-border pt-4">
            <div className="flex items-center gap-2 mb-3">
              <CreditCard className="w-4 h-4 text-[#13231E]" />
              <h3 className="font-semibold text-[#13231E]">Desglose de pagos</h3>
            </div>

            {loadingPayments ? (
              <div className="text-sm text-muted-foreground py-4">Cargando pagos…</div>
            ) : payments ? (
              <>
                <div className="grid grid-cols-3 gap-2 text-center mb-3">
                  <div className="rounded-md bg-[rgba(46,110,94,0.05)] p-2">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Presupuesto</div>
                    <div className="font-semibold text-[#13231E] tabular-nums">{formatEUR(payments.budget_total)}</div>
                  </div>
                  <div className="rounded-md bg-emerald-50 p-2">
                    <div className="text-[10px] uppercase tracking-wide text-emerald-700">Pagado</div>
                    <div className="font-semibold text-emerald-700 tabular-nums">{formatEUR(payments.paid_total)}</div>
                  </div>
                  {/* «Pendiente» solo va en verde si de verdad está al día:
                      con dinero devuelto al paciente que no cuelga de este
                      presupuesto (2026-09-22, G7) el 0 de aquí no lo descuenta,
                      y un verde diría «cobrado» de algo que se ha devuelto. */}
                  <div className={`rounded-md p-2 ${pendienteAlDia ? 'bg-emerald-50' : 'bg-amber-50'}`} data-testid="budget-payments-pendiente">
                    <div className={`text-[10px] uppercase tracking-wide ${pendienteAlDia ? 'text-emerald-700' : 'text-amber-700'}`}>Pendiente</div>
                    <div className={`font-semibold tabular-nums ${pendienteAlDia ? 'text-emerald-700' : 'text-amber-700'}`}>{formatEUR(payments.pending)}</div>
                  </div>
                </div>

                {/* Devuelto de más: «Pagado» se queda en 0 (un pagado negativo
                    no es un dato) y el exceso se dice aquí, en rojo, porque
                    es dinero que salió sin haber entrado por este presupuesto. */}
                {devueltoDeMas > 0 && (
                  <div className="mb-3 rounded-md bg-red-50 border border-red-200 p-2 text-xs text-red-800 flex items-start gap-2" data-testid="budget-payments-devuelto-de-mas">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                      Se han devuelto <strong className="tabular-nums">{formatEUR(devueltoDeMas)}</strong> más de lo cobrado
                      en este presupuesto: revisa los pagos y las devoluciones de abajo.
                    </div>
                  </div>
                )}

                {/* Lo que el servidor sabe y estas tres cifras no enseñan
                    (2026-09-22, G7): devoluciones del paciente sin presupuesto
                    asignado (no se sabe de cuál salió el dinero, así que no se
                    restan de ninguno) y cobros de OTRA ficha enganchados a este
                    presupuesto (no se suman: no son pagos de este paciente). */}
                {(devueltoSinPresupuesto > 0 || cobrosDeOtraFicha.length > 0) && (
                  <div className="mb-3 rounded-md bg-amber-50 border border-amber-200 p-2 text-xs text-amber-900 flex items-start gap-2" data-testid="budget-payments-aviso">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div className="space-y-1">
                      {devueltoSinPresupuesto > 0 && (
                        <div>
                          Además hay <strong className="tabular-nums">{formatEUR(devueltoSinPresupuesto)}</strong> devueltos
                          a este paciente sin presupuesto asignado: el «Pendiente» de arriba no los descuenta.
                        </div>
                      )}
                      {cobrosDeOtraFicha.length > 0 && (
                        <div>
                          {cobrosDeOtraFicha.length === 1
                            ? 'Hay 1 cobro enlazado a este presupuesto que es de otra ficha'
                            : `Hay ${cobrosDeOtraFicha.length} cobros enlazados a este presupuesto que son de otra ficha`}
                          {' '}({cobrosDeOtraFicha.map((c) => c.patient_name || c.patient_id).filter(Boolean).join(', ')}): no se suman como pagados.
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Pagos registrados */}
                {payments.payment_lines.length > 0 ? (
                  <div className="space-y-1 mb-3 max-h-[180px] overflow-y-auto">
                    {payments.payment_lines.map((ln, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 text-xs bg-white border border-border rounded-md px-2 py-1.5">
                        <div className="flex-1 min-w-0">
                          <span className="tabular-nums font-semibold text-[#13231E]">{formatEUR(ln.amount)}</span>
                          <span className="text-muted-foreground mx-1.5">·</span>
                          <span>{ln.method}</span>
                          {ln.concept && <span className="text-muted-foreground"> · {ln.concept}</span>}
                          <span className="text-muted-foreground"> · {formatDate(ln.date)}</span>
                        </div>
                        <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => deleteCobro(ln.cobro_id)} disabled={busy}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground bg-[rgba(46,110,94,0.08)] rounded-md p-2 mb-3">
                    Aún no se han registrado pagos. Añade el primero abajo.
                  </div>
                )}

                {/* Añadir nuevo pago */}
                <div className="rounded-md border border-[#2E6E5E]/30 bg-[rgba(46,110,94,0.04)] p-3">
                  <div className="text-xs font-semibold text-[#13231E] mb-2">Registrar nuevo pago parcial</div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                    <div>
                      <Label className="text-[10px]">Importe €</Label>
                      <Input
                        type="number"
                        list="budget-payment-amount-options"
                        step="0.01"
                        min="0"
                        value={newPayment.amount}
                        onChange={(e) => setNewPayment(p => ({ ...p, amount: e.target.value }))}
                        data-testid="budget-payment-amount"
                      />
                      <datalist id="budget-payment-amount-options">
                        {paymentAmountOptions.map((amount) => <option key={amount} value={amount} />)}
                      </datalist>
                    </div>
                    <div>
                      <Label className="text-[10px]">Método</Label>
                      <Select value={newPayment.payment_method} onValueChange={(v) => setNewPayment(p => ({ ...p, payment_method: v }))}>
                        <SelectTrigger data-testid="budget-payment-method"><SelectValue placeholder="— Elige el método —" /></SelectTrigger>
                        <SelectContent>{PAYMENT_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[10px]">Concepto (opcional)</Label>
                      <Input
                        list="budget-payment-concept-options"
                        value={newPayment.concept}
                        onChange={(e) => updatePaymentConcept(e.target.value)}
                        placeholder="Elige o escribe un concepto"
                        data-testid="budget-payment-concept"
                      />
                      <datalist id="budget-payment-concept-options">
                        {/* Aquí se añaden PAGOS del presupuesto: la devolución
                            (2026-09-08) se apunta desde Cobros. */}
                        {cobroPresets.filter((preset) => !preset.devolucion).map((preset) => (
                          <option key={preset.value} value={preset.value} label={preset.amountLabel} />
                        ))}
                      </datalist>
                    </div>
                    <div>
                      <Label className="text-[10px]">Fecha</Label>
                      <Input
                        type="date"
                        value={newPayment.date}
                        onChange={(e) => setNewPayment(p => ({ ...p, date: e.target.value }))}
                        data-testid="budget-payment-date"
                      />
                    </div>
                  </div>
                  <Button
                    onClick={addPayment}
                    disabled={busy}
                    className="mt-2 bg-[#2E6E5E] hover:bg-[#1E4A3F] w-full"
                    data-testid="budget-payment-add"
                  >
                    {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                    Añadir pago
                  </Button>
                </div>
              </>
            ) : null}
          </div>
        )}

        {/* MENSAJE SI RECHAZADO */}
        {isRejected && (
          <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <strong>Presupuesto rechazado.</strong> Quedará registrado para seguimiento.
              {bData.rejected_reason && <div className="mt-1 text-xs"><strong>Motivo:</strong> {bData.rejected_reason}</div>}
            </div>
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>

      {/* Agendar la cirugía del presupuesto: cita de quirófano con el primer
          hueco disponible ya preseleccionado y el procedimiento prefijado. */}
      <DarCitaDialog
        open={agendarOpen}
        onOpenChange={setAgendarOpen}
        patient={{ id: bData.patient_id, name: bData.patient_name }}
        titulo={`Agendar cirugía — ${bData.surgery_name || bData.patient_name || ''}`}
        soloQuirofano
        autoPrimerHueco
        defaultProcedimiento={bData.surgery_name || ''}
        onCreated={onCirugiaAgendada}
      />
    </Dialog>
  );
}
